import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {BreadCrumb} from "primereact/breadcrumb";
import {useTranslate} from "../../system/useTranslate";
import Icon from "../../system/components/icon";
import PageHeaderButton from "../components/page-header-button";
import {Button} from "primereact/button";
import {TabPanel, TabView} from "primereact/tabview";
import {Menu} from "primereact/menu";
import {useProject} from "../../models/project/use-project";
import {useTask} from "../../models/task/use-task";
import {setMessage, setWorkspaceMembership} from "../../system/app-slice";
import ProjectSettingsDialog from "./components/project-settings-dialog";
import Identification from "./tabs/identification";
import Calendar from "./tabs/calendar";
import FreeBoard from "./tabs/free-board";
import Tasks from "./tabs/tasks";
import Schedule from "./tabs/schedule";
import Dashboard from "./tabs/dashboard";
import ProjectSetupDock from "./components/project-setup-dock";
import Files from "./tabs/files";
import Message from "./tabs/message";
import Knowledge from "./tabs/knowledge";
import Meetings from "./tabs/meetings";
import TaskDialog from "../tasks/task-dialog";
import MeetingFormDialog, {peopleFromProject} from "./meetings/meeting-form-dialog";
import {MEETINGS_CHANGED} from "./meetings/project-meetings";
import {projectMeetingsApi} from "../../models/project/project-meetings-api";
import {usePermission} from "../../system/usePermission";
import Can from "../../system/can";
import ImportExcelTasks from "./components/import-excel-tasks";
import {useUserAccess} from "../../models/user-access/use-user-access";
import {
    mergeWorkspaceTasks,
    removeWorkspaceProject,
    workspaceGraphSelectors,
    upsertWorkspaceProject,
    upsertWorkspaceTask,
} from "../../system/workspace-graph-slice";
import SelectableTabsButton, {useSelectableTabs} from "../components/selectable-tabs-button";
import {buildProjectMemberRoleMutations} from "./project-member-roles";
import {filterMyProjectsScope, isProjectInMyProjectsScope} from "../../models/project/my-projects-scope";
import {
    filterProjectTabsByAccess,
    projectAccessRolesKey,
    resolveProjectAccess,
} from "../../models/project/project-access";
import {useWorkspaceRole} from "../../system/use-workspace-role";
import {usePublishAiScope} from "../../system/ai-scope-context";
import {normalizeApiError} from "../../system/api";

// The dashboard opens first (boss's task «تب داشبورد پروژه»); identification stays always visible (pinned), also for
// members who saved their own tab choice before the dashboard became the first tab. Meetings (boss's task «طراحی تب
// جلسات و مودال های ان», docs/tasks/26-project-meetings.md) are pinned too, so the header's «افزودن ← جلسه» always
// has its tab to open.
const PROJECT_TABS = [
    {id: "dashboard", label: "dashboard", icon: "mi mi:dashboard", view: Dashboard},
    {id: "identification", label: "identification", icon: "mi mi:fingerprint", view: Identification, pinned: true},
    {id: "free-board", label: "free-board", icon: "mi mi:dashboard_customize", view: FreeBoard},
    {id: "list", label: "list", icon: "mi mi:list", view: Tasks},
    {id: "meetings", label: "meetings", icon: "mi mi:groups", view: Meetings, pinned: true},
    {id: "schedule", label: "schedule", icon: "mi mi:timeline", view: Schedule, status: "disable", disabled: true},
    {id: "calendar", label: "calendar", icon: "mi mi:calendar_month", view: Calendar, status: "invisible"},
    {id: "message", label: "message", icon: "mi mi:chat_bubble", view: Message},
    {id: "knowledge", label: "project-knowledge", icon: "mi mi:lightbulb", view: Knowledge},
    {id: "files", label: "files", icon: "mi mi:folder_open", view: Files, status: "invisible", permission: "upload-file"},
    {id: "import-export", label: "import-export", icon: "mi mi:lab_profile", view: ImportExcelTasks, status: "invisible", permission: "upload-file"},
];

function taskMatchesProject(task, projectData) {
    if (!task || !projectData) return false;

    return Array.isArray(task.projects) && task.projects.some((projectItem) => (
        projectItem?.id === projectData.id || projectItem?.documentId === projectData.documentId
    ));
}

function Project() {
    const {workspaceMembershipData, workspaceId} = useSelector((state) => state.app);
    const workspaceProjects = useSelector(workspaceGraphSelectors.projects);
    const workspaceTasks = useSelector(workspaceGraphSelectors.tasks);
    const params = useParams();
    const [projectData, setProjectData] = useState();
    const [breadCrumb,setBreadCrumbData] = useState([]);
    const [projectDialogVisible,setProjectDialogVisible] = useState(false);
    const [projectSaving, setProjectSaving] = useState(false);
    const [projectSaveError, setProjectSaveError] = useState("");
    const [activeIndex,setActiveIndex] = useState(0);
    const [taskDialogVisible, setTaskDialogVisible] = useState(false);
    const [meetingDialogVisible, setMeetingDialogVisible] = useState(false);
    const addMenuRef = useRef(null);
    const location = useLocation();
    const translate = useTranslate();
    const navigate = useNavigate();
    const project = useProject();
    const dispatch = useDispatch();
    const [tasks, setTasks] = useState([]);
    const projectLoadedRef = useRef(false);
    const task = useTask();
    const permission = usePermission();
    const userRole = useUserAccess();
    const {userRoles} = useWorkspaceRole();
    const userRolesKey = useMemo(() => projectAccessRolesKey(userRoles), [userRoles]);
    const projectAccess = useMemo(() => resolveProjectAccess({
        workspace: workspaceMembershipData?.workspace,
        workspaceStatus: workspaceMembershipData?.workspace?.workspaceStatus,
        activeMembership: workspaceMembershipData,
        userRoles,
        project: projectData,
    }), [projectData, userRoles, workspaceMembershipData]);
    const tabs = filterProjectTabsByAccess(PROJECT_TABS, projectAccess);
    const {visibleTabs, selectedIds, toggleTab} = useSelectableTabs(
        tabs,
        `project:${params?.id || "unknown"}`,
        activeIndex,
        setActiveIndex
    );
    const showTab = (tabId) => {
        const index = visibleTabs.findIndex((tab) => tab.id === tabId);
        if (index < 0) return;
        setActiveIndex(index);
        setBreadCrumbData((prv) => {
            const next = Array.isArray(prv) ? [...prv] : [];
            next[2] = {label: translate(visibleTabs[index].label)};
            return next;
        });
    };

    // «?tab=meetings» (links of meeting notifications) opens that tab, once the project is loaded
    const requestedTab = new URLSearchParams(location.search).get("tab");
    useEffect(() => {
        if (requestedTab && projectData?.documentId) showTab(requestedTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedTab, projectData?.documentId, visibleTabs.length]);

    // the header's «افزودن»: a task or a meeting, from any tab of the project
    const canCreateMeeting = projectAccess.can("create-meeting");
    const addItems = [
        projectAccess.canCreateTask && {label: "کار", icon: <Icon icon="mi mi:add_task" className="text-base pl-2"/>, command: () => setTaskDialogVisible(true)},
        canCreateMeeting && {label: "جلسه", icon: <Icon icon="mi mi:event" className="text-base pl-2"/>, command: () => setMeetingDialogVisible(true)},
    ].filter(Boolean);
    const createMeetingFromHeader = async (values) => {
        // errors stay in the dialog (it shows them)
        await projectMeetingsApi.createMeeting(projectData.documentId, values);
        setMeetingDialogVisible(false);
        dispatch(setMessage({severity: "success", content: "جلسه‌ی جدید ساخته شد و دعوت‌شدگان مطلع شدند."}));
        showTab("meetings");
        window.dispatchEvent(new CustomEvent(MEETINGS_CHANGED, {detail: {project: projectData.documentId}}));
    };

    usePublishAiScope({
        activeTab: visibleTabs[activeIndex]?.id,
        entityType: "project",
        entityDocumentId: projectData?.documentId || params?.id,
        selectedEntityIds: [projectData?.documentId || params?.id].filter(Boolean),
    });

    useEffect(()=>{

        if (workspaceId && workspaceMembershipData?.documentId){
            getProject(params?.id);
        }
        // Model hook methods intentionally have unstable identities. The
        // serialized role key refreshes access only when role content changes,
        // not whenever the workspace graph receives an equivalent object.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[params?.id, workspaceId, workspaceMembershipData?.documentId]);
    // },[params?.id, workspaceId, workspaceMembershipData?.documentId, userRolesKey]);

    useEffect(() => {
        if (!params?.id || !Array.isArray(workspaceProjects)) return;

        const cached = workspaceProjects.find((item) => (
            (item.documentId === params?.id || item.id === params?.id)
            && isProjectInMyProjectsScope(
                item,
                workspaceMembershipData?.workspace,
                workspaceMembershipData,
                userRoles,
            )
        ));

        if (cached) {
            projectLoadedRef.current = true;

            setProjectData((current) => {
                const currentDocId = current?.documentId || current?.id;
                const cachedDocId = cached?.documentId || cached?.id;

                if (
                    currentDocId === cachedDocId &&
                    current?.updatedAt === cached?.updatedAt
                ) {
                    return current;
                }

                return current ? {...current, ...cached} : cached;
            });

            return;
        }

        // if (cached) {
        //     projectLoadedRef.current = true;
        //     setProjectData((current) => current ? {...current, ...cached} : cached);
        //     return;
        // }

        if (projectLoadedRef.current) {
            projectLoadedRef.current = false;
            setProjectData(null);
            setTasks([]);
        }
    }, [
        params?.id,
        workspaceProjects,
        workspaceMembershipData?.id,
        workspaceMembershipData?.documentId,
        workspaceMembershipData?.workspace?.id,
        workspaceMembershipData?.workspace?.documentId,
        userRoles,
    ]);

    useEffect(() => {
        if (!projectData?.id) {
            setTasks([]);
            return;
        }

        const nextTasks = Array.isArray(workspaceTasks)
            ? workspaceTasks.filter((item) => taskMatchesProject(item, projectData))
            : [];

        setTasks(nextTasks);
    }, [projectData?.id, projectData?.documentId, workspaceTasks]);

    useEffect(() => {
        if (projectData){
            permission.setScopeId(projectData?.documentId);
            permission.setScopeRolePermissions(projectData?.rolesConfig);

        }
    }, [projectData]);

    const refreshProject = () => getProject(params.id);

    const getProject = async (projectId)=>{
        let _data = await project.getProject(projectId);
        if (!_data || !isProjectInMyProjectsScope(
            _data,
            workspaceMembershipData?.workspace,
            workspaceMembershipData,
            userRoles,
        )) {
            setProjectData(null);
            setTasks([]);
            return null;
        }
        _data.deliverables = (_data.deliverables || []).filter(
            d => !d.archivedAt
        );
        // console.log(_data.deliverables);
        setProjectData(_data);
        dispatch(upsertWorkspaceProject(_data));
        setBreadCrumbData([
            {label:translate("projects"), command:()=>{navigate("/projects")}},
            {label:_data?.title},
            {label:translate(visibleTabs[activeIndex]?.label || visibleTabs[0]?.label)}
        ]);

        if (_data) getTasks(_data?.id);
    }

    // useEffect(() => {
    //     if (projectData) getTasks();
    // }, [projectData, workspaceMembershipData]);

    const updateProject = async (projectData, updateData, payload)=>{
        const updateFields = Object.keys(updateData || {});
        const isMembersOnlyUpdate = Boolean(payload?.members)
            && updateFields.every((field) => field === "members");
        const isArchiveUpdate = updateFields.length > 0
            && updateFields.every((field) => ["archivedAt", "archivedBy"].includes(field));
        const canUpdateProject = isArchiveUpdate
            ? projectAccess.canArchiveProject
            : isMembersOnlyUpdate
                ? projectAccess.canManageMembers
                : projectAccess.canEditProject;
        if (!canUpdateProject) {
            permission.can(
                isArchiveUpdate
                    ? "archive-project"
                    : isMembersOnlyUpdate
                        ? "manage-project-members"
                        : "modify-project-settings",
                true,
            );
            return null;
        }
        setProjectSaving(true);
        setProjectSaveError("");
        try {
            let updatedProject = await project.updateProject(
                projectData.documentId,
                updateData,
                {throwOnError: true},
            );
            if (!updatedProject) throw new Error("project.error.update-failed");

            if (payload?.members){
                const {upserts, removals} = buildProjectMemberRoleMutations({
                    previousMemberships: payload.previousMembers || projectData.members,
                    entries: payload.members,
                    project: projectData,
                    rolesConfig: updateData?.rolesConfig ?? projectData?.rolesConfig,
                });

                await Promise.all([
                    ...upserts.map(({membership, existingRole, role, permissions}) => {
                        const roleData = {
                            workspace_membership: membership,
                            role,
                            permissions,
                        };
                        return existingRole
                            ? updateUserRole(existingRole, roleData)
                            : createUserRole(roleData);
                    }),
                    ...removals.map((role) => userRole.removeUserAccess(role.documentId || role.id)),
                ]);

                // The project update response is generated before its scoped roles
                // are written. Reload once so member.user_roles reflects the saved UI.
                updatedProject = await project.getProject(projectData.documentId) || updatedProject;
            }

            if (!isProjectInMyProjectsScope(
                updatedProject,
                workspaceMembershipData?.workspace,
                workspaceMembershipData,
                userRoles,
            )) {
                dispatch(removeWorkspaceProject(updatedProject));
                setProjectData(null);
                setTasks([]);
                navigate("/projects");
                return updatedProject;
            }

            dispatch(upsertWorkspaceProject(updatedProject));
            setProjectData(updatedProject);
            dispatch(setWorkspaceMembership({
                ...workspaceMembershipData,
                workspace: {
                    ...workspaceMembershipData.workspace,
                    projects: filterMyProjectsScope(
                        (workspaceMembershipData.workspace?.projects || []).map((item) =>
                            item.documentId === updatedProject?.documentId || item.id === updatedProject?.id
                                ? {...item, ...updatedProject}
                                : item
                        ),
                        workspaceMembershipData.workspace,
                        workspaceMembershipData,
                        userRoles,
                    ),
                }
            }));
            setProjectDialogVisible(false);
            return updatedProject;
        } catch (requestError) {
            const errorKey = normalizeApiError(requestError)?.message || "project.error.update-failed";
            setProjectSaveError(translate(errorKey));
            return null;
        } finally {
            setProjectSaving(false);
        }
    }

    const createUserRole = async (userRoleData)=>{
       if (!projectAccess.canManageProjectRoles) return;
       return await userRole.createUserAccess({...userRoleData, scope_type:"project", scope_id:projectData?.documentId, workspace:workspaceMembershipData?.workspace.id})
    }

    const updateUserRole = async (userRoleData, updateData)=>{
       if (!projectAccess.canManageProjectRoles) return;
       return await userRole.updateUserAccess(userRoleData.documentId || userRoleData.id,{...updateData, scope_type:"project", scope_id:projectData?.documentId, workspace:workspaceMembershipData?.workspace.id})
    }

    const createTask = async (taskData)=>{
        if (!projectAccess.canCreateTask) {
            permission.can("create-task", true);
            return;
        }
        if (taskData){
            taskData = {...taskData, workspace:workspaceMembershipData.workspace.id, projects:{connect:[{id:projectData.id}]}}
            const createdTask = await task.createTask(taskData);
            if (createdTask) {
                dispatch(upsertWorkspaceTask(createdTask));
                setTasks((current) => current.some((item) => item.documentId === createdTask.documentId || item.id === createdTask.id)
                    ? current
                    : [...current, createdTask]);
            }
            return createdTask;
        }
    }

    const createBatchTasks = async (tasks, filename)=>{
        if (!projectAccess.canImportTasks) {
            permission.can(
                projectAccess.canUploadFile ? "create-task" : "upload-file",
                true,
            );
            return [];
        }
        const extra = {type:"excel-imported", filename}
        const result = await Promise.all(
            tasks.map(st =>{
                return task.createTask({...st, projects: [projectData?.id], assignee: st.assignee, workspace:workspaceMembershipData.workspace.id, extra}).then((created) => {
                    dispatch(upsertWorkspaceTask(created));
                    return created;
                });
            })
        );
        dispatch(setMessage({severity:"success", content:translate("excel-task-imported-successfully")}));
        return result;
    }

    const updateTask = async (taskData, updateData, subTasks=[])=>{
        const updatedFields = Object.keys(updateData || {});
        const isStatusOnlyUpdate = updatedFields.length > 0 && updatedFields.every((field) => (
            ["taskStatus", "doneAt", "sortIndex", "freeBoardColumnId", "freeBoardSortIndex"].includes(field)
        ));
        const containsNewSubTask = subTasks.some((subTask) => !subTask?.documentId);
        const canUpdate = isStatusOnlyUpdate
            ? projectAccess.canChangeTaskStatus
            : projectAccess.canUpdateTask;
        if (!canUpdate || (containsNewSubTask && !projectAccess.canCreateTask)) {
            permission.can(
                containsNewSubTask && !projectAccess.canCreateTask
                    ? "create-task"
                    : isStatusOnlyUpdate
                        ? "change-task-status"
                        : "edit-task",
                true,
            );
            return;
        }
        let updatedTask = null;
        if (updateData){
            updatedTask = await task.updateTask(taskData.documentId, updateData);
            if (updatedTask) dispatch(upsertWorkspaceTask(updatedTask));
        }
        const _subTasks = await Promise.all(
            subTasks.map(st =>{
                delete st?.id;

                if (st?.documentId){
                    let documentId = st?.documentId;
                    delete st?.documentId;
                    delete st?.createdAt;
                    delete st?.updatedAt;
                    // console.log(st);
                    return task.updateTask(documentId, {...st, dueDate: st.dueDate?(Number.isInteger(st.dueDate)?new Date(st.dueDate*1000).toISOString():st.dueDate):undefined}).then((updated) => {
                        dispatch(upsertWorkspaceTask(updated));
                        return updated;
                    });
                }
                else {
                    // console.log(st);
                    return task.createTask({...st, parentTask: taskData.id, dueDate: st.dueDate?(Number.isInteger(st.dueDate)?new Date(st.dueDate*1000).toISOString():st.dueDate):undefined, workspace:workspaceMembershipData.workspace.id}).then((created) => {
                        dispatch(upsertWorkspaceTask(created));
                        return created;
                    });
                }
            })
        );

        setTasks((current) => {
            let nextTasks = [...current];

            // a deleted (archived) task leaves the list at once
            if (updatedTask && (updateData?.archivedAt || updatedTask.archivedAt)) {
                nextTasks = nextTasks.filter((item) => item.documentId !== updatedTask.documentId && item.id !== updatedTask.id);
            } else if (updatedTask) {
                const updatedIndex = nextTasks.findIndex((item) => item.documentId === updatedTask.documentId || item.id === updatedTask.id);
                if (updatedIndex >= 0) {
                    nextTasks[updatedIndex] = {...nextTasks[updatedIndex], ...updatedTask};
                } else {
                    nextTasks.push(updatedTask);
                }
            }

            _subTasks.filter(Boolean).forEach((subTask) => {
                const subTaskIndex = nextTasks.findIndex((item) => item.documentId === subTask.documentId || item.id === subTask.id);
                if (subTaskIndex >= 0) {
                    nextTasks[subTaskIndex] = {...nextTasks[subTaskIndex], ...subTask};
                } else {
                    nextTasks.push(subTask);
                }
            });

            return nextTasks;
        });
        return updatedTask;
        // dispatch(setMessage({severity:"success", content:translate("task-updated")}))
    }


    // const updateTask = async (taskData, updateData, refresh=true)=>{
    //     if (updateData){
    //         let _data = await task.updateTask(taskData.documentId, updateData);
    //     }
    //     if (refresh)await getTasks();
    //     // setTasks(prvData=>{
    //     //     let newData = [...prvData];
    //     //     let itemIndex = prvData.findIndex(e=>e.documentId===_data.documentId);
    //     //     newData[itemIndex] = {...newData[itemIndex], ..._data}
    //     //     return newData;
    //     // })
    // }

    const getTasks = async (projectId)=>{

        const _tasks = await task.getTasks(projectId, workspaceMembershipData?.workspace?.id)

        setTasks(_tasks);
        dispatch(mergeWorkspaceTasks(_tasks));
        return _tasks;
    }


    return (
        <div className="h-full flex flex-column min-h-0">
            {projectData && (
                <BreadCrumb
                    separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>}
                    className="bg-transparent border-none text-xs"
                    model={breadCrumb}
                />
            )}

            <PageHeaderButton
                end={
                    <div className="flex gap-2 align-items-center">
                        {addItems.length > 0 && (
                            <>
                                <Button className="text-xs p-2" icon={<Icon icon="mi mi:add" className="text-sm pl-2"/>} label="افزودن"
                                        aria-haspopup="menu" aria-controls="project-add-menu" onClick={(event) => addMenuRef.current?.toggle(event)}/>
                                <Menu id="project-add-menu" ref={addMenuRef} model={addItems} popup className="rtl text-xs" pt={{menuitem: {className: "text-xs"}}}/>
                            </>
                        )}
                        <Can permission="share-project" scopeId={projectData?.documentId} scopeRolePermissions={projectData?.rolesConfig}>
                            <Button className="text-xs p-button-secondary p-button-outlined p-2" icon={<Icon icon="mi mi:share" className="text-sm pl-2"/>} label={translate("share-project")}/>
                        </Can>
                        <Can permission="modify-project-settings" scopeId={projectData?.documentId} scopeRolePermissions={projectData?.rolesConfig}>
                            <Button className="text-xs p-2" icon={<Icon icon="mi mi:edit" className="text-sm pl-2"/>} label={translate("edit-project")} onClick={()=>{setProjectSaveError("");setProjectDialogVisible(true)}}/>
                        </Can>
                        </div>
                }
                title={projectData?.title||""}
                // description={projectData?.description}
            />

            {/* tabs */}
            <div className="flex-1 min-h-0 relative">
                <TabView
                    lazy
                    activeIndex={activeIndex}
                    onTabChange={(e)=>{
                        setActiveIndex(e.index);
                        setBreadCrumbData(prv=>{
                            const next = Array.isArray(prv) ? [...prv] : [];
                            next[2] = {label:translate(visibleTabs[e.index].label)};
                            return next;
                        });
                    }}
                    panelContainerClassName="flex-1 min-h-0 p-0 border-noround"
                    className="h-full flex flex-column">
                    {visibleTabs.map((tab, tabIndex) => {
                        const TabContent = tab.view;

                        return (
                            <TabPanel
                                key={tab.id}
                                header={translate(tab.label)}
                                disabled={tab.disabled}
                                leftIcon={<Icon icon={tab.icon} className="pl-2" />}
                                className="h-full text-xs">
                                {tabIndex === activeIndex && (
                                    <div className="h-full min-h-0 overflow-hidden">
                                        <TabContent
                                            refreshProject={refreshProject}
                                            projectData={projectData}
                                            tasks={tasks}
                                            createTask={createTask}
                                            createBatchTasks={createBatchTasks}
                                            updateTask={updateTask}
                                            updateProject={updateProject}
                                            projectAccess={projectAccess}
                                        />
                                    </div>
                                )}
                            </TabPanel>
                        );
                    })}
                </TabView>

                {!projectAccess.isReadOnly && (
                    <SelectableTabsButton tabs={tabs} selectedIds={selectedIds} onChange={toggleTab}
                                          className="absolute z-5" style={{top: "0.45rem", left: "0.75rem"}}/>
                )}
            </div>

            <TaskDialog
                visible={taskDialogVisible}
                setVisible={setTaskDialogVisible}
                payload={{taskData: {}}}
                onSubmit={(taskData) => {
                    createTask(taskData);
                    setTaskDialogVisible(false);
                }}
            />
            {meetingDialogVisible && (
                <MeetingFormDialog
                    visible={meetingDialogVisible}
                    onHide={() => setMeetingDialogVisible(false)}
                    people={peopleFromProject(projectData)}
                    onSave={createMeetingFromHeader}
                />
            )}

            {/* «داک تکمیل پروژه»: the six parts that make a project clear (docs/tasks/24-project-setup-dock.md) */}
            <ProjectSetupDock
                projectData={projectData}
                updateProject={updateProject}
                refreshProject={refreshProject}
                canManage={Boolean(projectAccess.canEditProject) && !projectAccess.isReadOnly}
                onOpenTasks={() => {
                    const index = visibleTabs.findIndex((tab) => tab.id === "list");
                    const board = visibleTabs.findIndex((tab) => tab.id === "free-board");
                    if (index >= 0 || board >= 0) setActiveIndex(index >= 0 ? index : board);
                }}
            />

            <ProjectSettingsDialog
                visible={projectDialogVisible}
                projectData={projectData}
                setVisible={setProjectDialogVisible}
                submitting={projectSaving}
                error={projectSaveError}
                onSubmit={(e)=>{updateProject(projectData, e.value, e.payload)}}
                onArchive={() => updateProject(projectData, {
                    archivedAt: new Date().toISOString(),
                    archivedBy: workspaceMembershipData,
                })}
            />

        </div>
    );
}

export default Project;
